import { useRef } from 'react';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import type { SurveyAnswer } from '../types/survey';

export type AnswerStatus = 'idle' | 'correct' | 'wrong' | 'dimmed';

interface AnswerCardProps {
  answer: SurveyAnswer;
  position: number;
  status: AnswerStatus;
  disabled: boolean;
  onSelect: (position: number) => void;
}

export function AnswerCard({
  answer,
  position,
  status,
  disabled,
  onSelect,
}: AnswerCardProps) {
  const cardRef = useRef<HTMLButtonElement>(null);

  useGSAP(
    () => {
      const card = cardRef.current;
      if (!card) return;

      const liquid = card.querySelector<HTMLElement>('.answer-card__liquid');
      const icon = card.querySelector<HTMLElement>('.answer-card__icon');
      const glint = card.querySelector<HTMLElement>('.answer-card__glint');

      if (status === 'idle') {
        gsap.set(liquid, { yPercent: 110 });
        gsap.set(icon, { opacity: 0, scale: 0.35, rotation: -20 });
        return;
      }

      if (status === 'correct' || status === 'wrong') {
        const timeline = gsap.timeline({ defaults: { ease: 'power3.out' } });
        timeline
          .fromTo(liquid, { yPercent: 110 }, { yPercent: 0, duration: 0.7 })
          .to(card, {
            y: status === 'correct' ? -8 : -4,
            scale: status === 'correct' ? 1.025 : 1,
            duration: 0.32,
          }, 0.08)
          .fromTo(icon, {
            opacity: 0,
            scale: 0.35,
            rotation: -20,
          }, {
            opacity: 1,
            scale: 1,
            rotation: 0,
            duration: 0.42,
            ease: 'back.out(1.8)',
          }, 0.32)
          .fromTo(glint, { xPercent: -120 }, { xPercent: 520, duration: 0.85 }, 0.28);
      } else {
        gsap.to(card, {
          opacity: 0.38,
          scale: 0.97,
          duration: 0.28,
          ease: 'power2.out',
        });
      }
    },
    { scope: cardRef, dependencies: [status] },
  );

  const className = [
    'answer-card',
    status === 'correct' ? 'is-correct' : '',
    status === 'wrong' ? 'is-wrong' : '',
    status === 'dimmed' ? 'is-dimmed' : '',
  ].filter(Boolean).join(' ');

  return (
    <button
      ref={cardRef}
      type="button"
      className={className}
      disabled={disabled}
      aria-label={`Réponse ${position}`}
      aria-pressed={status === 'correct' || status === 'wrong'}
      onClick={() => onSelect(position)}
    >
      <span className="answer-card__liquid" />
      <span className="answer-card__glint" />
      <span className="answer-card__number">{position}</span>
      <span className="answer-card__icon" aria-hidden="true">
        {status === 'correct' ? '✓' : status === 'wrong' ? '×' : ''}
      </span>
      <span className="answer-card__copy">
        <span className="answer-card__fr">{answer.text.fr}</span>
        <span className="answer-card__ar" dir="rtl">{answer.text.ar}</span>
      </span>
    </button>
  );
}
